import React, { useState, useEffect } from 'react';
import {
  DataTable,
  SkeletonText,
  Search,
  Pagination as PaginationComp,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TableHeader,
  TableToolbar,
  TableBatchActions,
  TableBatchAction,
  TableToolbarContent,
  TableExpandedRow,
  TableSelectAll,
  TableSelectRow,
} from '@carbon/react';
import './GenericDataTable.css';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { MdDragIndicator } from 'react-icons/md';
import { motion } from 'framer-motion';
import { ChevronDown } from '@carbon/icons-react';
import { arrMap } from '../../utils/reactMapUtils';

const getSize = () => {
  return [window.innerWidth, window.innerHeight];
};

export const Pagination = ({
  onChange,
  pageSizes,
  pageSize,
  page = 0,
  totalItems = 0,
  ...props
}) => {
  return (
    <PaginationComp
      onChange={onChange}
      pageSizes={pageSizes ?? [10, 20, 30, 40, 50]}
      pageSize={pageSize}
      page={page}
      totalItems={totalItems}
      itemsPerPageText="Items per page"
      itemRangeText={() =>
        `${1 + Number(page - 1) * Number(pageSize)} - ${Math.min(
          Number(totalItems),
          Number(pageSize) * Number(page)
        )} of ${Number(totalItems)} items`
      }
      pageRangeText={() =>
        `of ${Math.ceil(Number(totalItems) / Number(pageSize))} page`
      }
      {...props}
    />
  );
};

const GenericDataTable = (props) => {
  const {
    loading,
    loadSkCount,
    rows = [],
    borderstyle = {},
    searchable,
    searchValue = '',
    selectable: selectableG,
    draggable: draggableG,
    expandable: expandableG,
    onDragEnd,
    expandComp,
    headers = [],
    batchActions = [],
    otherActions = [],
    title,
    expandedRows = [],
    customSorting,
    batchActionClick,
    onSearchInputChange,
    className,
    noHeaders = false,
    tableStyle = {},
    trClass = '',
    tdClass = '',
    tbodyClass = '',
    isSortable = false,
    placeHolderText = '',
    tableWrapClassName = '',
    extras = {},
    mobileHeader,
    noHeadersForMobile = false,
    mobileClassName = '',
    mobileHeaderStyles = '',
    mobileTableStyles = {},
    showScrollBar = false,
  } = props;

  const [searchVal, setSearchVal] = useState('');

  useEffect(() => {
    setSearchVal(searchValue);
  }, [searchValue]);

  const [exObj, setExObj] = useState({});

  const rowDataArr = rows.map((row) => ({
    id: row?.id,
    rowProps: row?.rowProps,
    onClick: row?.onClick,
  }));
  const draggable =
    draggableG && !rowDataArr.every((x) => x.rowProps?.draggable === false);
  const expandable =
    expandableG && !rowDataArr.every((x) => x.rowProps?.expandable === false);
  const selectable =
    selectableG && !rowDataArr.every((x) => x.rowProps?.selectable === false);

  useEffect(() => {
    if (expandable) {
      const expObj = Object.fromEntries(
        rows.map((x, i) => [`isRowExpanded-${i}`, x?.isExpanded])
      );
      setExObj(expObj);
    }
  }, [JSON.stringify(rowDataArr)]);

  const onSearchChange = (e) => {
    const sVal = e.target.value;
    setSearchVal(sVal);
    return onSearchInputChange?.({ target: { value: sVal } });
  };

  const customSortBy = (header, sortBy) => {
    sortBy(header.key);
    customSorting({ propName: header.colpropName });
  };

  const getDisabledStatus = (row) => {
    let rowContentsArr = (row?.id || '').toString().split('_') ?? [];
    if (
      rowContentsArr &&
      Array.isArray(rowContentsArr) &&
      rowContentsArr.length > 0
    ) {
      if (rowContentsArr[2] && rowContentsArr[3] == 'true') return true;
      return false;
    }
    return false;
  };

  const scrollBarCondition =
    (searchable || otherActions.length > 0 || batchActions.length > 0) &&
    (rows.length > 0 || headers.length > 3);

  const renderTable = ({
    rows = [],
    headers = [],
    hideHeadersEmpty = false,
    isMobileView = false,
  }) => {
    return (
      <>
        {loading ? (
          <DataTable
            rows={Array.from({ length: loadSkCount ?? 8 }, ($, i) => ({
              id: i.toString(),
              header_0: <SkeletonText />,
              header_1: <SkeletonText />,
            }))}
            headers={Array.from({ length: 2 }, (_, i) => ({
              header: '',
              key: `header_${i}`,
            }))}
            render={({
              rows,
              headers,
              getHeaderProps,
              getRowProps,
              getTableProps,
              getSelectionProps,
              getBatchActionProps,
              sortBy,
              selectedRows,
            }) => {
              return (
                <TableContainer title={title} className={className}>
                  {(searchable ||
                    (otherActions && otherActions.length > 0) ||
                    (batchActions && batchActions.length > 0)) && (
                    <TableToolbar>
                      {searchable && (
                        <Search
                          id="search_table"
                          name="searchValueTable"
                          value={searchVal}
                          placeHolderText={placeHolderText}
                          onChange={onSearchChange}
                        />
                      )}
                      <TableToolbarContent></TableToolbarContent>
                    </TableToolbar>
                  )}
                  <div
                    className={`${
                      !loading && showScrollBar && rows.length > 0
                        ? 'x-table-scroll'
                        : scrollBarCondition
                        ? 'x-table-scroll-nt'
                        : ''
                    } ${tableWrapClassName}`}>
                    <Table style={tableStyle} {...getTableProps()}>
                      {noHeaders || (hideHeadersEmpty && !selectable) ? null : (
                        <TableHead>
                          <TableRow>
                            {headers.map((header, i) => (
                              <TableHeader {...getHeaderProps({ header })}>
                                {header?.header}
                              </TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                      )}
                      <TableBody className={tbodyClass}>
                        {rows?.map((row, index) => (
                          <TableRow
                            style={borderstyle}
                            id={`${row.id}`}
                            key={`${row.id}`}
                            className={trClass}>
                            {row.cells.map((cell, cellIndex) => (
                              <TableCell
                                colSpan={1}
                                className={`${tdClass}`}
                                key={cell.id}
                                id={cell.id}>
                                {cell.value}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TableContainer>
              );
            }}
          />
        ) : draggable ? (
          <DragDropContext
            onDragEnd={(res) => {
              if (!onDragEnd) return;
              const { destination, source, reason } = res;
              if (!destination || reason === 'CANCEL') return;
              if (
                destination.droppableId === source.droppableId &&
                destination.index === source.index
              )
                return;
              onDragEnd(res);
            }}>
            <DataTable
              rows={rows.map((el, ind) =>
                !el ? { id: `rowhide-${ind}` } : el
              )}
              headers={headers}
              sortRow={() => {}}
              isSortable={isSortable}
              render={({
                rows,
                headers,
                getHeaderProps,
                getRowProps,
                getTableProps,
                getSelectionProps,
                getBatchActionProps,
                sortBy,
                selectedRows,
              }) => {
                return (
                  <TableContainer title={title} className={className}>
                    {(searchable ||
                      (otherActions && otherActions.length > 0) ||
                      (batchActions && batchActions.length > 0)) && (
                      <TableToolbar>
                        {selectedRows.length > 0 && batchActions && (
                          <TableBatchActions
                            {...getBatchActionProps()}
                            className={`${
                              arrMap(batchActions).length > 4
                                ? 'xpa-actionlist-position d-block'
                                : ''
                            }`}
                            cancel="Cancel">
                            {batchActions &&
                              batchActions.map((eachBatchAction, key) => (
                                <TableBatchAction
                                  id={eachBatchAction?.key}
                                  key={key}
                                  renderIcon={eachBatchAction.icon}
                                  iconDescription="Action"
                                  onClick={(e) => {
                                    const selectedItems = rowDataArr.filter(
                                      (x) =>
                                        selectedRows.find((y) => y.id == x.id)
                                    );
                                    batchActionClick(
                                      selectedItems,
                                      eachBatchAction.key
                                    );
                                  }}>
                                  {eachBatchAction.label}
                                </TableBatchAction>
                              ))}
                          </TableBatchActions>
                        )}
                        {searchable && (
                          <Search
                            id="search_table"
                            name="searchValueTable"
                            value={searchVal}
                            placeHolderText={placeHolderText}
                            onChange={onSearchChange}
                          />
                        )}
                        <TableToolbarContent>
                          {otherActions &&
                            otherActions.map((eachAction) => eachAction)}
                        </TableToolbarContent>
                      </TableToolbar>
                    )}
                    <div
                      className={`${
                        !loading && showScrollBar && rows.length > 0
                          ? 'x-table-scroll'
                          : scrollBarCondition
                          ? 'x-table-scroll-nt'
                          : ''
                      } ${tableWrapClassName}`}>
                      <Table
                        style={isMobileView ? mobileTableStyles : tableStyle}
                        {...getTableProps()}>
                        {noHeaders ||
                        (hideHeadersEmpty && !selectable) ? null : (
                          <TableHead>
                            <TableRow>
                              <TableHeader
                                className="actionable"
                                style={{ width: 5 }}
                              />
                              {expandable && (
                                <TableHeader
                                  className="actionable"
                                  style={{ width: 5 }}
                                />
                              )}
                              {selectable && (
                                <TableSelectAll
                                  className="actionable sticky"
                                  {...getSelectionProps()}
                                />
                              )}
                              {headers.map((header) => (
                                <TableHeader
                                  {...getHeaderProps({ header })}
                                  onClick={(e) => customSortBy(header, sortBy)}
                                  className={header.className ?? ''}>
                                  {header?.header}
                                </TableHeader>
                              ))}
                            </TableRow>
                          </TableHead>
                        )}
                        <Droppable droppableId="value">
                          {(provided, snapshot) => (
                            <tbody
                              className={tbodyClass}
                              {...provided.droppableProps}
                              ref={provided.innerRef}>
                              {rows.map((row, index) => {
                                if (!row) return null;
                                if (`${row.id}`.includes('rowhide'))
                                  return null;
                                const { rowProps } =
                                  rowDataArr.find((x) => x.id === row.id) ?? {};
                                const {
                                  selectable: selble = true,
                                  expandable: expble = true,
                                  draggable: drable = true,
                                } = rowProps ?? {};
                                return (
                                  <Draggable
                                    key={`${row.id}`}
                                    index={index}
                                    draggableId={`${row.id}`}
                                    isDragDisabled={!drable}>
                                    {(provided, snapshot) => (
                                      <>
                                        <tr
                                          className={trClass}
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          style={{
                                            ...borderstyle,
                                            ...provided.draggableProps.style,
                                            left: 'auto !important',
                                            top: 'auto !important',
                                          }}>
                                          <td
                                            className="actionable"
                                            style={{ width: 5 }}
                                            {...provided.dragHandleProps}>
                                            <div
                                              style={{
                                                width: 12,
                                                marginLeft: -5,
                                                marginTop: -8,
                                              }}>
                                              {drable && (
                                                <MdDragIndicator
                                                  style={{ fill: 'grey' }}
                                                />
                                              )}
                                            </div>
                                          </td>
                                          {expandable &&
                                            (expble ? (
                                              <td className="actionable">
                                                <div
                                                  style={{
                                                    width: 12,
                                                    marginLeft: -5,
                                                    marginTop: -8,
                                                  }}>
                                                  <motion.button
                                                    animate={{
                                                      rotate: exObj[
                                                        `isRowExpanded-${index}`
                                                      ]
                                                        ? 180
                                                        : 0,
                                                    }}
                                                    transition={{
                                                      duration: 0.3,
                                                    }}
                                                    className="tw-ml-1 cursor-pointer focus:tw-outline-none"
                                                    onClick={() =>
                                                      setExObj((p) => ({
                                                        ...p,
                                                        [`isRowExpanded-${index}`]:
                                                          exObj[
                                                            `isRowExpanded-${index}`
                                                          ]
                                                            ? false
                                                            : true,
                                                      }))
                                                    }>
                                                    <ChevronDown
                                                      size={16}
                                                      style={{ fill: 'grey' }}
                                                    />
                                                  </motion.button>
                                                </div>
                                              </td>
                                            ) : (
                                              <td className="actionable">
                                                <div
                                                  style={{
                                                    width: 12,
                                                    marginLeft: -5,
                                                    marginTop: -8,
                                                  }}></div>
                                              </td>
                                            ))}
                                          {selectable &&
                                            (selble ? (
                                              <TableSelectRow
                                                className="actionable sticky"
                                                {...getSelectionProps({
                                                  row,
                                                  disabled:
                                                    getDisabledStatus(row),
                                                })}
                                              />
                                            ) : (
                                              <td className="actionable sticky">
                                                <div
                                                  style={{
                                                    width: 12,
                                                    marginLeft: -5,
                                                    marginTop: -8,
                                                  }}></div>
                                              </td>
                                            ))}

                                          {row.cells.map((cell, cellIndex) => (
                                            <TableCell
                                              colSpan={
                                                headers[cellIndex]?.colSpan ?? 1
                                              }
                                              className={`${
                                                headers[cellIndex]?.className ??
                                                ''
                                              } ${tdClass}`}
                                              key={cell.id}
                                              id={cell.id}>
                                              {cell.value}
                                            </TableCell>
                                          ))}
                                        </tr>
                                        {expandable &&
                                          expble &&
                                          exObj[`isRowExpanded-${index}`] !==
                                            undefined && (
                                            <TableExpandedRow
                                              colSpan={
                                                row.cells.length +
                                                1 +
                                                (selectable ? 1 : 0) +
                                                (expandable ? 1 : 0)
                                              }
                                              id={`expand-${row.id}`}
                                              style={borderstyle}
                                              className={`${
                                                !exObj[`isRowExpanded-${index}`]
                                                  ? 'd-none'
                                                  : ''
                                              }`}>
                                              {expandComp
                                                ? typeof expandComp ===
                                                  'function'
                                                  ? expandComp({
                                                      expId: row.id,
                                                      ...rowDataArr.find(
                                                        (x) => row.id == x.id
                                                      ),
                                                    })
                                                  : expandComp
                                                : expandedRows[index]}
                                            </TableExpandedRow>
                                          )}
                                      </>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </tbody>
                          )}
                        </Droppable>
                      </Table>
                    </div>
                  </TableContainer>
                );
              }}
            />
          </DragDropContext>
        ) : (
          <DataTable
            rows={rows.map((el, ind) => (!el ? { id: `rowhide-${ind}` } : el))}
            headers={headers}
            sortRow={() => {}}
            isSortable={isSortable}
            render={({
              rows,
              headers,
              getHeaderProps,
              getRowProps,
              getTableProps,
              getSelectionProps,
              getExpandHeaderProps,
              getBatchActionProps,
              sortBy,
              selectedRows,
            }) => {
              return (
                <TableContainer title={title} className={className}>
                  {(searchable ||
                    (otherActions && otherActions.length > 0) ||
                    (batchActions && batchActions.length > 0)) && (
                    <TableToolbar>
                      {selectedRows.length > 0 && batchActions && (
                        <TableBatchActions
                          {...getBatchActionProps()}
                          className={`${
                            arrMap(batchActions).length > 4
                              ? 'xpa-actionlist-position d-block'
                              : ''
                          }`}>
                          {batchActions &&
                            batchActions.map((eachBatchAction, key) => (
                              <TableBatchAction
                                id={eachBatchAction?.key}
                                key={key}
                                renderIcon={eachBatchAction.icon}
                                iconDescription="Action"
                                onClick={(e) => {
                                  const selectedItems = rowDataArr.filter((x) =>
                                    selectedRows.find((y) => y.id == x.id)
                                  );
                                  batchActionClick(
                                    selectedItems,
                                    eachBatchAction.key
                                  );
                                }}>
                                {eachBatchAction.label}
                              </TableBatchAction>
                            ))}
                        </TableBatchActions>
                      )}
                      {searchable && (
                        <Search
                          id="search_table"
                          name="searchValueTable"
                          value={searchVal}
                          placeHolderText={placeHolderText}
                          onChange={onSearchChange}
                        />
                      )}
                      <TableToolbarContent>
                        {otherActions &&
                          otherActions.map((eachAction) => eachAction)}
                      </TableToolbarContent>
                    </TableToolbar>
                  )}
                  <div
                    className={`${
                      !loading && showScrollBar && rows.length > 0
                        ? 'x-table-scroll'
                        : scrollBarCondition
                        ? 'x-table-scroll-nt'
                        : ''
                    } ${tableWrapClassName}`}>
                    <Table
                      style={isMobileView ? mobileTableStyles : tableStyle}
                      {...getTableProps()}>
                      {noHeaders || (hideHeadersEmpty && !selectable) ? null : (
                        <TableHead>
                          <TableRow>
                            {expandable && (
                              <TableHeader
                                className="actionable"
                                style={{ width: 5 }}
                              />
                            )}
                            {selectable && (
                              <TableSelectAll
                                className="actionable sticky"
                                {...getSelectionProps()}
                              />
                            )}
                            {headers.map((header) => (
                              <TableHeader
                                {...getHeaderProps({ header })}
                                onClick={(e) => customSortBy(header, sortBy)}
                                className={
                                  header.className ? header.className : ''
                                }>
                                {header?.header}
                              </TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                      )}
                      <TableBody className={tbodyClass}>
                        {rows.map((row, index) => {
                          if (!row) return null;
                          if (`${row.id}`.includes('rowhide')) return null;

                          const { rowProps, onClick } =
                            rowDataArr.find((x) => x.id === row.id) ?? {};

                          const {
                            selectable: selble = true,
                            expandable: expble = true,
                          } = rowProps ?? {};
                          return (
                            <>
                              <TableRow
                                style={borderstyle}
                                id={`${row.id}`}
                                key={`${row.id}`}
                                className={trClass}>
                                {expandable &&
                                  (expble ? (
                                    <td className="actionable">
                                      <div
                                        style={{
                                          width: 12,
                                          marginLeft: -5,
                                          marginTop: -8,
                                        }}>
                                        <motion.button
                                          animate={{
                                            rotate: exObj[
                                              `isRowExpanded-${index}`
                                            ]
                                              ? 180
                                              : 0,
                                          }}
                                          transition={{ duration: 0.3 }}
                                          className="tw-ml-1 cursor-pointer focus:tw-outline-none"
                                          onClick={() =>
                                            setExObj((p) => ({
                                              ...p,
                                              [`isRowExpanded-${index}`]: exObj[
                                                `isRowExpanded-${index}`
                                              ]
                                                ? false
                                                : true,
                                            }))
                                          }>
                                          <ChevronDown
                                            size={16}
                                            style={{ fill: 'grey' }}
                                          />
                                        </motion.button>
                                      </div>
                                    </td>
                                  ) : (
                                    <td className="actionable">
                                      <div
                                        style={{
                                          width: 12,
                                          marginLeft: -5,
                                          marginTop: -8,
                                        }}></div>
                                    </td>
                                  ))}
                                {selectable &&
                                  (selble ? (
                                    <TableSelectRow
                                      className="actionable sticky"
                                      {...getSelectionProps({
                                        row,
                                        disabled: getDisabledStatus(row),
                                      })}
                                    />
                                  ) : (
                                    <td className="actionable sticky">
                                      <div
                                        style={{
                                          width: 12,
                                          marginLeft: -5,
                                          marginTop: -8,
                                        }}></div>
                                    </td>
                                  ))}
                                {row.cells.map((cell, cellIndex) => (
                                  <TableCell
                                    colSpan={headers[cellIndex]?.colSpan ?? 1}
                                    className={`${
                                      headers[cellIndex]?.className ?? ''
                                    } ${tdClass}`}
                                    key={cell.id}
                                    id={cell.id}
                                    onClick={
                                      typeof onClick === 'function'
                                        ? onClick
                                        : undefined
                                    }>
                                    {cell.value}
                                  </TableCell>
                                ))}
                              </TableRow>
                              {expandable &&
                                expble &&
                                exObj[`isRowExpanded-${index}`] !==
                                  undefined && (
                                  <TableExpandedRow
                                    colSpan={
                                      row.cells.length +
                                      (selectable ? 1 : 0) +
                                      (expandable ? 1 : 0)
                                    }
                                    id={`expand-${row.id}`}
                                    style={borderstyle}
                                    className={`${
                                      !exObj[`isRowExpanded-${index}`]
                                        ? 'd-none'
                                        : ''
                                    }`}>
                                    {expandComp
                                      ? typeof expandComp === 'function'
                                        ? expandComp({
                                            expId: row.id,
                                            ...rowDataArr.find(
                                              (x) => row.id == x.id
                                            ),
                                          })
                                        : expandComp
                                      : expandedRows[index]}
                                  </TableExpandedRow>
                                )}
                            </>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </TableContainer>
              );
            }}
          />
        )}
      </>
    );
  };

  if (getSize()[0] <= 768) {
    return (
      <div className="container-layout">
        {renderTable({
          isMobileView: true,
          hideHeadersEmpty: [undefined, false].includes(mobileHeader),
          headers: [{ header: mobileHeader || '', key: `header_i` }],
          rows: rows.map((row, rId) => {
            if (!row) return null;
            return {
              id: row.id,
              header_i: (
                <div
                  key={rId}
                  name={row.id}
                  className={`mobile-card mt-3 mb-4 ${mobileClassName ?? ''}`}>
                  {arrMap(headers, (head, hId) => {
                    const { header, key } = head ?? {};
                    return (
                      <>
                        {!noHeadersForMobile && (
                          <div
                            className={`cds--col cds--type-zeta d-inline-flex align-items-center pl-0 pr-0 ${mobileHeaderStyles}`}>
                            {header?.header}
                            {/* <span className='ml-2'>:</span> */}
                          </div>
                        )}
                        <div
                          className={`${key} ${
                            extras[key]?.className ?? ''
                          } cds--col pl-0 pr-0`}
                          title={key.toLowerCase()}>
                          {row[key]}
                        </div>
                      </>
                    );
                  }).joinElm(<hr className="mt-1 mb-2" />)}
                </div>
              ),
            };
          }),
        })}
      </div>
    );
  }

  return (
    <div className="container-layout">{renderTable({ headers, rows })}</div>
  );
};

export default GenericDataTable;
